import React, { useEffect, useState } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import actionCreators from "@modules/shared/actionCreator";
import { route } from "@routes/helper";
import * as sharedSelectors from "@modules/shared/selectors";
import { View, Text, GSGrid, Button, ConfirmationModal, Loader } from "@components";

type OwnProps = $Exact<{
  clientId: number,
  alertId: number,
}>;

type PropsFromState = $Exact<{
  alertSuppressionIsLoading: boolean,
  alertsSuppressedBy: Array<Object>,
  alertsSuppression: Array<Object>,
  alertsSuppressionOptions: Array<Object>,
}>;

type PropsFromDispatch = $Exact<{
  getAlertsSuppressedBy: typeof actionCreators.GetAlertsSuppressedBy,
  getAlertsSuppression: typeof actionCreators.GetAlertsSuppression,
  deleteAlertsSuppression: typeof actionCreators.DeleteAlertsSuppression,
  addAlertsSuppression: typeof actionCreators.AddAlertsSuppression,
  getAlertsSuppressionOptions: typeof actionCreators.GetAlertsSuppressionOptions,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export function AlertSuppression(props: Object) {
  const [showAddAlertToSuppressModal, setShowAddAlertToSuppressModal] = useState(false);
  const [confirmDeleteAlert, setConfirmDeleteAlert] = useState(false);
  const { clientId, alertId } = props;

  useEffect(() => {
    props.getAlertsSuppressedBy({ clientId, alertId });
    props.getAlertsSuppression({ clientId, alertId });
    props.getAlertsSuppressionOptions({ clientId, alertId });
  }, [clientId, alertId]);

  useEffect(() => {
    showAddAlertToSuppressModal && props.getAlertsSuppressionOptions({ clientId, alertId });
  }, [showAddAlertToSuppressModal]);

  function showConfirmDeleteSuppressedAlert(alert) {
    setConfirmDeleteAlert(alert);
  }
  function hideConfirmDeleteSuppressedAlert() {
    setConfirmDeleteAlert(false);
  }

  function deleteSuppressedAlert() {
    props.deleteAlertsSuppression({
      clientId,
      alertId,
      suppressionAlertId: confirmDeleteAlert.id,
    });
    setConfirmDeleteAlert(false);
  }

  function toggleAddAlertToSuppressModal() {
    setShowAddAlertToSuppressModal(!showAddAlertToSuppressModal);
  }
  function addAlertToSuppress(id: number) {
    props.addAlertsSuppression({
      clientId,
      alertId,
      suppressionAlertId: id,
    });
  }

  return (
    <View className="gs-container" flex={1} gap="6">
      <View direction="row" alignItems="center" justifyContent="space-between">
        <h6>{props.t("ALERT_SUPPRESSION")}</h6>
      </View>
      {props.alertSuppressionIsLoading && <Loader />}
      <View gap="2">
        <Text type="h6" text={props.t("THIS_ALERT_IS_SUPPRESSED_BY")} />
        <GSGrid
          maxHeight={400}
          rows={props.alertsSuppressedBy}
          columns={[
            {
              name: props.t("ALERT_ID"),
              key: "alertId",
              width: 60,
              renderCell: ({ row }: Object) => (
                <a
                  href={route("ADMINISTRATION_SERVICE_EVENT_DETAILS", {
                    clientId: props.clientId,
                    alertId: row.alertId,
                  })}
                  target="_blank"
                  rel="noreferrer"
                  className="pointer text-decoration-underline"
                >
                  {row.alertId}
                </a>
              ),
            },
            { name: props.t("ALERT_NAME"), key: "alertName", align: "left" },
          ]}
        />
      </View>
      <View gap="2">
        <View direction="row" justifyContent="space-between" alignItems="center">
          <Text type="h6" text={props.t("ALERTS_THIS_ALERT_WILL_SUPPRESS")} />

          <Button onClick={toggleAddAlertToSuppressModal} materialIcon="add_box" label="Add" />
        </View>
        <Text className="message-box" text={props.t("ALERTS_THIS_ALERT_WILL_SUPPRESS_INFO_BOX")} />
        <GSGrid
          maxHeight={400}
          rows={props.alertsSuppression}
          columns={[
            {
              name: props.t("ALERT_ID"),
              key: "alertId",
              width: 60,
              renderCell: ({ row }: Object) => (
                <a
                  href={route("ADMINISTRATION_SERVICE_EVENT_DETAILS", {
                    clientId: props.clientId,
                    alertId: row.alertId,
                  })}
                  target="_blank"
                  rel="noreferrer"
                  className="pointer text-decoration-underline"
                >
                  {row.alertId}
                </a>
              ),
            },
            { name: props.t("ALERT_NAME"), key: "alertName", align: "left" },
            {
              name: "Remove",
              key: "delete",
              width: 80,
              onCellClick: (row: Object) => showConfirmDeleteSuppressedAlert(row),
              renderCell: (row: Object) => GSGrid.render.materialIcon("delete"),
            },
          ]}
        />
      </View>

      {confirmDeleteAlert && (
        <ConfirmationModal
          modalSize="small"
          headerText={props.t("ARE_YOU_SURE")}
          bodyContent={
            <View>
              <Text
                text={`${props.t("ARE_YOU_SURE_YOU_WANT_TO_DELETE_ALERT")}${
                  confirmDeleteAlert.id
                } "${confirmDeleteAlert.alertName}"`}
              />
            </View>
          }
          confirmBtnLabel={props.t("DELETE")}
          confirmBtnColor="negative"
          onConfirm={deleteSuppressedAlert}
          cancelBtnLabel={props.t("CLOSE")}
          onCancel={hideConfirmDeleteSuppressedAlert}
        />
      )}

      {showAddAlertToSuppressModal && (
        <ConfirmationModal
          modalSize="large"
          headerText={props.t("ADD_ALERT_TO_SUPPRESS")}
          bodyContent={
            <View gap="2">
              <Text
                className="message-box"
                text={props.t("THIS_ALERT_IS_SUPPRESSED_BY_INFO_BOX")}
              />
              <GSGrid
                maxHeight={400}
                rows={props.alertsSuppressionOptions}
                columns={[
                  {
                    name: props.t("ALERT_ID"),
                    key: "alertId",
                    width: 60,
                    renderCell: ({ row }: Object) => (
                      <a
                        href={route("ADMINISTRATION_SERVICE_EVENT_DETAILS", {
                          clientId: props.clientId,
                          alertId: row.alertId,
                        })}
                        target="_blank"
                        rel="noreferrer"
                        className="pointer text-decoration-underline"
                      >
                        {row.alertId}
                      </a>
                    ),
                  },
                  { name: props.t("ALERT_NAME"), key: "alertName", align: "left" },
                  {
                    name: props.t("AVAILABILITY"),
                    key: "isAvailableForSuppression",
                    align: "left",

                    renderCell: ({ row }: Object) => {
                      return (
                        <Text
                          color={row.isAvailableForSuppression ? "positive" : "negative"}
                          text={props.t(row.availabilityDescription)}
                        />
                      );
                    },
                  },
                  {
                    name: props.t("ADD"),
                    key: "add",
                    width: 50,
                    onCellClick: (row: Object) => {
                      row.isAvailableForSuppression && addAlertToSuppress(row.alertId);
                    },
                    renderCell: ({ row }: Object) => {
                      return row.isAvailableForSuppression ? GSGrid.render.materialIcon("add") : "";
                    },
                  },
                ]}
              />
            </View>
          }
          cancelBtnLabel={props.t("CLOSE")}
          onCancel={toggleAddAlertToSuppressModal}
        />
      )}
    </View>
  );
}

const mapStateToProps = (state: State): PropsFromState => ({
  alertSuppressionIsLoading: sharedSelectors.alertSuppressionIsLoading(state),
  alertsSuppressedBy: sharedSelectors.getAlertsSuppressedBy(state),
  alertsSuppression: sharedSelectors.getAlertsSuppression(state),
  alertsSuppressionOptions: sharedSelectors.getAlertsSuppressionOptions(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getAlertsSuppressedBy: actionCreators.GetAlertsSuppressedBy,
  getAlertsSuppression: actionCreators.GetAlertsSuppression,
  deleteAlertsSuppression: actionCreators.DeleteAlertsSuppression,
  addAlertsSuppression: actionCreators.AddAlertsSuppression,
  getAlertsSuppressionOptions: actionCreators.GetAlertsSuppressionOptions,
};
export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(AlertSuppression);
